import React from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { LogItem, ObjectTypeChoices } from '@/models/logItem';
import styles from './AuditTable.module.css';

interface AuditTableProps {
  logItems?: LogItem[];
  total?: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onTableChange: TableProps<LogItem>['onChange'];
  onRowClick?: (record: LogItem) => void;
}

function deriveResult(actionType: string | null): 'Success' | 'Failed' {
  return actionType?.includes('FAILURE') ? 'Failed' : 'Success';
}

function renderObject(objectType: string | null, objectId: string | null) {
  if (!objectId || !objectType) return '—';
  const label = objectType;
  if (objectType === ObjectTypeChoices.STUDY) {
    return (
      <Link to={`/studies/${objectId}`} onClick={(e) => e.stopPropagation()}>
        {label}
      </Link>
    );
  }
  if (objectType === ObjectTypeChoices.REPORT) {
    return (
      <Link to={`/reports/${objectId}`} onClick={(e) => e.stopPropagation()}>
        {label}
      </Link>
    );
  }
  if (objectType === ObjectTypeChoices.USER) {
    return (
      <Link to="/admin/users" onClick={(e) => e.stopPropagation()}>
        {label}
      </Link>
    );
  }
  return label;
}

const AuditTable: React.FC<AuditTableProps> = ({
  logItems,
  total,
  page,
  pageSize,
  loading,
  onTableChange,
  onRowClick,
}) => {
  const columns: ColumnsType<LogItem> = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (text: string) => dayjs(text).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'User',
      key: 'user',
      render: (_: unknown, record: LogItem) => {
        const name =
          `${record.userFirstName ?? ''} ${record.userLastName ?? ''}`.trim();
        return name || record.userId || '—';
      },
    },
    {
      title: 'Action',
      dataIndex: 'actionType',
      key: 'actionType',
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Object',
      key: 'object',
      render: (_: unknown, record: LogItem) =>
        renderObject(record.objectType, record.objectId),
    },
    {
      title: 'Result',
      key: 'result',
      width: 90,
      render: (_: unknown, record: LogItem) => {
        const result = deriveResult(record.actionType);
        return (
          <Tag color={result === 'Failed' ? 'error' : 'success'}>{result}</Tag>
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (v: string | null) => v ?? '—',
    },
  ];

  return (
    <div className={styles.panel}>
      <Table<LogItem>
        columns={columns}
        dataSource={logItems}
        rowKey="uuid"
        sticky
        style={{ borderRadius: 10 }}
        loading={loading}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total: total ?? 0,
          showSizeChanger: true,
          showTotal: (t, [s, e]) => `${s}-${e} of ${t}`,
        }}
      />
    </div>
  );
};

export default AuditTable;
